import { commonUtils } from '@/helpers/commonUtils'
import styles from './PdfPage4.module.less'
import PdfPage, { PageHeader } from '../PdfPage/PdfPage'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GENDER_MAPS, INDUSTRY_CATEGORY_MAPS, TARGET_MARKTS_MAPS } from '@/i18nEnumConsts'
import cls from 'classnames'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import { getIndustryDisplayName } from '@/helpers/industry'

interface Props {
  project: ProjectModels.ProjectDetail
  onRendered: (cnt: number) => void
  no: number
}

function PdfPage4(props: Props) {
  const { t, i18n } = useTranslation()
  const { onRendered, project } = props
  const isPortraitLayout = isPortraitByProjectInfo(project)

  const [brandIndustry, setBrandIndustry] = useState<string>(props.project.brandIndustry)
  const [gender, setGender] = useState<string>(props.project.adCustomerGender)
  const [targetMarket, setTargetMarket] = useState(props.project.adTargetMarket)

  useEffect(() => {
    const lng = i18n.language as keyof typeof INDUSTRY_CATEGORY_MAPS
    const industryIngMap = INDUSTRY_CATEGORY_MAPS[lng] as Record<string, string>
    setBrandIndustry(getIndustryDisplayName(props.project.brandIndustry, industryIngMap))
    const genderMap = GENDER_MAPS[lng] as Record<string, string>
    setGender(genderMap[props.project.adCustomerGender] || props.project.adCustomerGender)
    const targetMarketMap = TARGET_MARKTS_MAPS[lng] as Record<string, string>
    setTargetMarket(targetMarketMap[props.project.adTargetMarket] || props.project.adTargetMarket)
  }, [i18n.language])

  useEffect(() => {
    onRendered(1)
  }, [])

  return (
    <PdfPage bgSrc={commonUtils.getImagePath('PDFDownload/pdf-page-4/PDF-bg-4.jpg')} no={props.no}>
      <div className={styles.pdfPage4}>
        <PageHeader
          project={props.project}
          text={'01.' + t('dashboard.projectDetailPage.basicInformation')}
        />
        <div className={cls(styles.content, { [styles.portraitContent]: isPortraitLayout })}>
          {isPortraitLayout ? (
            <>
              <div className={styles.portraitLeft}>
                <p className={styles.portraitMainTitle}>{props.project.adName}</p>
                <div className={styles.portraitInfo}>
                  <div className={styles.list}>
                    <div className={styles.title}>
                      {t('dashboard.projectDetailPage.basicInformation')}
                    </div>
                    <div className={styles.item}>
                      <p className={styles.label}>
                        {t('dashboard.projectDetailPage.infoDrawer.projectName')}:
                      </p>
                      <p className={styles.value}>{props.project.projectName}</p>
                    </div>
                    <div className={styles.item}>
                      <p className={styles.label}>
                        {t('dashboard.projectDetailPage.infoDrawer.brandName')}:
                      </p>
                      <p className={styles.value}>{props.project.brandName}</p>
                    </div>
                    <div className={styles.item}>
                      <p className={styles.label}>
                        {t('dashboard.projectDetailPage.infoDrawer.category')}:
                      </p>
                      <p className={styles.value}>{brandIndustry}</p>
                    </div>
                  </div>

                  <div className={styles.list}>
                    <div className={styles.title}>
                      {t('dashboard.projectDetailPage.infoDrawer.advertisingInformation')}
                    </div>
                    <div className={styles.item}>
                      <p className={styles.label}>
                        {t('dashboard.projectDetailPage.infoDrawer.adName')}:
                      </p>
                      <p className={styles.value}>{props.project.adName}</p>
                    </div>

                    <div className={styles.item}>
                      <p className={styles.label}>
                        {t('dashboard.projectDetailPage.infoDrawer.targetMarket')}:
                      </p>
                      <p className={styles.value}>{targetMarket}</p>
                    </div>

                    <div className={styles.item}>
                      <p className={styles.label}>
                        {t('dashboard.projectDetailPage.infoDrawer.taGender')}:
                      </p>
                      <p className={styles.value}>{gender}</p>
                    </div>

                    <div className={styles.item}>
                      <p className={styles.label}>{t('dashboard.projectDetailPage.infoDrawer.taAge')}:</p>
                      <p className={styles.value}>
                        {t('dashboard.projectDetailPage.infoDrawer.ageRange', {
                          start: project.adCustomerAgeStart,
                          end: project.adCustomerAgeEnd,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.portraitRight}>
                <div className={styles.portraitPhoneCard}>
                  <div className={styles.portraitPhoneInner}>
                    <img className={styles.cover} src={props.project.coverPath} />
                  </div>
                  <img
                    className={styles.portraitPass}
                    src={
                      project.globalMetrics.overall.score >= project.globalMetrics.overall.meanScore
                        ? commonUtils.getImagePath('PDFDownload/pdf-page-4/pass.png')
                        : commonUtils.getImagePath('PDFDownload/pdf-page-4/not-pass.png')
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.left}>
                <p className={styles.title}>{props.project.adName}</p>

                <div className={styles.info}>
                  <img
                    className={styles.pass}
                    src={
                      project.globalMetrics.overall.score >= project.globalMetrics.overall.meanScore
                        ? commonUtils.getImagePath('PDFDownload/pdf-page-4/pass.png')
                        : commonUtils.getImagePath('PDFDownload/pdf-page-4/not-pass.png')
                    }
                  />
                  <div className={styles.coverWrap}>
                    <img className={styles.cover} src={props.project.coverPath} />
                  </div>
                </div>
              </div>

              <div className={styles.right}>
                <div className={styles.list}>
                  <div className={styles.title}>
                    {t('dashboard.projectDetailPage.basicInformation')}
                  </div>
                  <div className={styles.item}>
                    <p className={styles.label}>
                      {t('dashboard.projectDetailPage.infoDrawer.projectName')}:
                    </p>
                    <p className={styles.value}>{props.project.projectName}</p>
                  </div>
                  <div className={styles.item}>
                    <p className={styles.label}>
                      {t('dashboard.projectDetailPage.infoDrawer.brandName')}:{' '}
                    </p>
                    <p className={styles.value}>{props.project.brandName}</p>
                  </div>
                  <div className={styles.item}>
                    <p className={styles.label}>
                      {t('dashboard.projectDetailPage.infoDrawer.category')}:{' '}
                    </p>
                    <p className={styles.value}>{brandIndustry}</p>
                  </div>
                </div>

                <div className={styles.list} style={{ marginTop: '86px' }}>
                  <div className={styles.title}>
                    {t('dashboard.projectDetailPage.infoDrawer.advertisingInformation')}
                  </div>
                  <div className={styles.item}>
                    <p className={styles.label}>
                      {t('dashboard.projectDetailPage.infoDrawer.adName')}:
                    </p>
                    <p className={styles.value}>{props.project.adName}</p>
                  </div>

                  <div className={styles.item}>
                    <p className={styles.label}>
                      {t('dashboard.projectDetailPage.infoDrawer.targetMarket')}:
                    </p>
                    <p className={styles.value}>{targetMarket}</p>
                  </div>

                  <div className={styles.item}>
                    <p className={styles.label}>
                      {t('dashboard.projectDetailPage.infoDrawer.taGender')}:
                    </p>
                    <p className={styles.value}>{gender}</p>
                  </div>

                  <div className={styles.item}>
                    <p className={styles.label}>{t('dashboard.projectDetailPage.infoDrawer.taAge')}:</p>
                    <p className={styles.value}>
                      {t('dashboard.projectDetailPage.infoDrawer.ageRange', {
                        start: project.adCustomerAgeStart,
                        end: project.adCustomerAgeEnd,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PdfPage>
  )
}

export default PdfPage4
